import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useToast } from '@/components/common/toast';
import { Select2Field } from '@/components/forms/Select2Field';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { TextField } from '@/components/forms/TextField';
import { ApiError } from '@/api/client/abpError';
import type { TenantReportInformationUpdateDto } from '@/api/types/tenants';
import { TabActions, TabShell } from './TabShell';
import { useTenantReports, useUpdateReports } from '../api';
import { useTenantEnumOptions } from '../enumOptions';

type ReportsForm = Record<string, string>;

const TEXT_FIELDS: Array<{ name: keyof TenantReportInformationUpdateDto; label: string }> = [
  { name: 'trsIdentificationCodeOfEntity', label: 'TRS Identification Code Of Entity' },
  { name: 'postalAddress', label: 'Postal Address' },
  { name: 'faxNumber', label: 'Fax Number' },
  { name: 'website', label: 'Website' },
  { name: 'icfMemberName', label: 'ICF Member Name' },
  { name: 'cifLicenseNumber', label: 'CIF License Number' },
  { name: 'address', label: 'Address' },
  { name: 'street', label: 'Street' },
  { name: 'building', label: 'Building' },
  { name: 'district', label: 'District' },
  { name: 'city', label: 'City' },
  { name: 'tin', label: 'Tin' },
  { name: 'inType', label: 'In Type' },
];

const NUMBER_FIELDS: Array<{ name: keyof TenantReportInformationUpdateDto; label: string }> = [
  { name: 'nameOfConsolidatingEntity', label: 'Name Of Consolidating Entity' },
  { name: 'balanceSheetTotal', label: 'Balance Sheet Total' },
  { name: 'combinedTotalAnnualGrossVenue', label: 'Combined Total Annual Gross Venue' },
  { name: 'totalAnnualGrossVenue', label: 'Total Annual Gross Venue' },
  { name: 'totalExpensesOfProfits', label: 'Total Expenses Of Profits' },
  { name: 'ownCashDeposit', label: 'Own Cash Deposit' },
];

export function ReportsTab({ tenantId, onCancel }: { tenantId: string; onCancel: () => void }) {
  const toast = useToast();
  const reportsQuery = useTenantReports(tenantId);
  const updateMut = useUpdateReports(tenantId);
  const { register, handleSubmit, reset, control } = useForm<ReportsForm>();
  const { accountingFrameworkOptions, levelOfApplicationOptions, reasonForFallingOptions } =
    useTenantEnumOptions();

  useEffect(() => {
    const d = reportsQuery.data;
    if (!d) return;
    const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));
    reset({
      ...Object.fromEntries(TEXT_FIELDS.map((f) => [f.name, str(d[f.name])])),
      ...Object.fromEntries(NUMBER_FIELDS.map((f) => [f.name, str(d[f.name])])),
      reportingEntityCode: str(d.reportingEntityCode),
      accountingFramework: str(d.accountingFramework),
      levelOfApplication: str(d.levelOfApplication),
      reasonForFalling: str(d.reasonForFalling),
      mifirStartingDate: d.mifirStartingDate ? d.mifirStartingDate.slice(0, 10) : '',
      monthlyAccountingStartingDate: d.monthlyAccountingStartingDate
        ? d.monthlyAccountingStartingDate.slice(0, 10)
        : '',
    });
  }, [reportsQuery.data, reset]);

  function onSubmit(values: ReportsForm) {
    const num = (v: string) => (v.trim() === '' ? null : Number(v));
    const txt = (v: string) => (v.trim() === '' ? null : v);
    const payload: TenantReportInformationUpdateDto = {
      tenantId,
      trsIdentificationCodeOfEntity: txt(values.trsIdentificationCodeOfEntity),
      postalAddress: txt(values.postalAddress),
      faxNumber: txt(values.faxNumber),
      website: txt(values.website),
      address: txt(values.address),
      street: txt(values.street),
      building: txt(values.building),
      district: txt(values.district),
      city: txt(values.city),
      tin: txt(values.tin),
      inType: txt(values.inType),
      icfMemberName: txt(values.icfMemberName),
      cifLicenseNumber: txt(values.cifLicenseNumber),
      reportingEntityCode: txt(values.reportingEntityCode),
      monthlyAccountingStartingDate: txt(values.monthlyAccountingStartingDate),
      mifirStartingDate: txt(values.mifirStartingDate),
      accountingFramework: Number(values.accountingFramework),
      levelOfApplication: Number(values.levelOfApplication),
      reasonForFalling: Number(values.reasonForFalling),
      nameOfConsolidatingEntity: txt(values.nameOfConsolidatingEntity),
      balanceSheetTotal: num(values.balanceSheetTotal),
      combinedTotalAnnualGrossVenue: num(values.combinedTotalAnnualGrossVenue),
      totalAnnualGrossVenue: num(values.totalAnnualGrossVenue),
      totalExpensesOfProfits: num(values.totalExpensesOfProfits),
      ownCashDeposit: num(values.ownCashDeposit),
    };
    updateMut.mutate(payload, {
      onSuccess: () => toast.success('Reports information saved.'),
      onError: (err) =>
        toast.error(err instanceof ApiError ? err.message : 'Failed to save reports information.'),
    });
  }

  return (
    <TabShell
      isLoading={reportsQuery.isLoading}
      isError={reportsQuery.isError}
      onRetry={() => reportsQuery.refetch()}
    >
      <TabActions busy={updateMut.isPending} onCancel={onCancel} formId="clientReportForm" />
      <form id="clientReportForm" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="row mt-3">
          {TEXT_FIELDS.map((f) => (
            <div className="col-lg-4" key={f.name}>
              <TextField label={f.label} {...register(f.name)} />
            </div>
          ))}
          <div className="col-lg-4">
            <TextField
              label="Reporting Entity Code"
              maxLength={6}
              pattern="^\d{6}$"
              {...register('reportingEntityCode')}
            />
          </div>
          <div className="col-lg-4">
            <Select2Field
              control={control}
              name="accountingFramework"
              label="Accounting Framework"
              options={accountingFrameworkOptions}
            />
          </div>
          <div className="col-lg-4">
            <Select2Field
              control={control}
              name="levelOfApplication"
              label="Level Of Application"
              options={levelOfApplicationOptions}
            />
          </div>
          <div className="col-lg-4">
            <Select2Field
              control={control}
              name="reasonForFalling"
              label="Reason For Falling"
              options={reasonForFallingOptions}
            />
          </div>
          {NUMBER_FIELDS.map((f) => (
            <div className="col-lg-4" key={f.name}>
              <TextField label={f.label} type="number" step="any" {...register(f.name)} />
            </div>
          ))}
          <div className="col-12 col-lg-6">
            <h6>Mifir Information</h6>
            <Controller
              control={control}
              name="mifirStartingDate"
              render={({ field }) => (
                <DateRangePicker
                  mode="single"
                  label="Mifir Starting Date"
                  value={field.value ?? null}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          <div className="col-12 col-lg-6">
            <h6>Monthly Accounting Information</h6>
            <Controller
              control={control}
              name="monthlyAccountingStartingDate"
              render={({ field }) => (
                <DateRangePicker
                  mode="single"
                  label="Monthly Accounting Starting Date"
                  value={field.value ?? null}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </div>
      </form>
    </TabShell>
  );
}
